import React, { useEffect, useState } from "react";
import { Award, Clock, Hourglass, PlayCircle } from "lucide-react";
import { AppButton, Callout, WhatsAppIcon } from "../../ui";
import ProtectedVideoPlayer from "../../Common/ProtectedVideoPlayer";
import CertificateShareActions from "./CertificateShareActions";

const formatMinutes = (seconds) => {
  const m = Math.round((Number(seconds) || 0) / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
};


/**
 * PostSessionSummary — what an attendee needs once the session is over, above
 * the feedback form every follow-up link lands on:
 *
 *   - attended and earned it  → their certificate: download, verify, LinkedIn;
 *   - earned it, not issued   → "being prepared", so nobody wonders;
 *   - attended, fell short    → how long we counted, and the bar, said plainly;
 *   - never joined            → the recording, when one is shared.
 *
 * `me` is GET /sessions/:slug/me. Download links are short-lived, so one is
 * fetched when the button is pressed rather than on load.
 *
 * A recording stored in the LMS plays here, in pieces if the host stopped and
 * restarted it; a link an admin pasted opens in its own tab, as it always did.
 */
const PostSessionSummary = ({ me, api, slug, token }) => {
  const [cert, setCert] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [recordings, setRecordings] = useState(null);
  const [part, setPart] = useState(0);
  const certState = me?.certificate || {};

  useEffect(() => {
    if (!certState.issued || !token) return undefined;
    let cancelled = false;
    api.getCertificate(slug, token)
      .then(({ data }) => { if (!cancelled) setCert(data.certificate); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [certState.issued, slug, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async () => {
    setBusy("download");
    setError("");
    try {
      const { data } = await api.getCertificate(slug, token);
      window.location.assign(data.downloadUrl);
    } catch {
      setError("The download could not be prepared. Please try again in a moment.");
    }
    setBusy("");
  };

  const watch = async () => {
    setBusy("recording");
    setError("");
    try {
      const { data } = await api.getRecording(slug, token);
      if (data.recordings?.length) setRecordings(data.recordings);
      else window.open(data.url, "_blank", "noopener");
    } catch (err) {
      setError(err?.response?.data?.message || "The recording is not available yet.");
    }
    setBusy("");
  };

  // The links expire; the player asks for fresh ones and keeps its place.
  const refreshLinks = async () => {
    try {
      const { data } = await api.getRecording(slug, token);
      if (data.recordings?.length) setRecordings(data.recordings);
    } catch {
      // The player offers "Try again".
    }
  };

  const attendance = me?.attendance;
  const sections = [];

  if (certState.enabled && certState.issued) {
    sections.push(
      <div key="cert" className="tw-rounded tw-border tw-border-warning/30 tw-bg-warning-soft tw-p-4">
        <div className="tw-flex tw-items-start tw-gap-3">
          <Award size={28} className="tw-shrink-0 tw-text-warning-fg" aria-hidden="true" />
          <div className="tw-min-w-0">
            <p className="tw-m-0 tw-text-base tw-font-semibold tw-text-content">Your certificate is ready</p>
            <p className="tw-m-0 tw-text-sm tw-text-content-muted">
              Certificate of Participation · ID <span className="tw-font-mono">{certState.certificateNo}</span>
            </p>
          </div>
        </div>
        <div className="tw-mt-3">
          <CertificateShareActions
            cert={cert || { certificateNo: certState.certificateNo, verifyUrl: certState.verifyUrl }}
            onDownload={download}
            downloading={busy === "download"}
            showVerify
          />
        </div>
      </div>,
    );
  } else if (certState.enabled && certState.pending) {
    sections.push(
      <Callout key="pending" tone="success" icon={<Hourglass size={14} />}>
        You attended {attendance?.percent ?? ""}% — your certificate is being prepared and will arrive by email and WhatsApp shortly.
      </Callout>,
    );
  } else if (certState.enabled && me?.attended && me?.sessionStatus === "ended" && attendance) {
    sections.push(
      <Callout key="short" tone="info" icon={<Clock size={14} />}>
        We counted {formatMinutes(attendance.attendedSeconds)} ({attendance.percent}%) for you. Certificates go to attendees
        present for at least {certState.minAttendancePct}% of the session, so this one did not qualify.
      </Callout>,
    );
  }

  if (!me?.attended && me?.hasRecording && recordings) {
    const current = recordings[part] || recordings[0];
    sections.push(
      <div key="rec-player" className="tw-flex tw-flex-col tw-gap-3">
        {recordings.length > 1 && (
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {recordings.map((row, index) => (
              <AppButton
                key={row._id}
                size="sm"
                variant={index === part ? "default" : "outline"}
                aria-pressed={index === part}
                onClick={() => setPart(index)}
              >
                Part {index + 1}
              </AppButton>
            ))}
          </div>
        )}
        <ProtectedVideoPlayer
          src={current.url}
          poster={current.thumbnailUrl}
          mediaKey={current._id}
          onExpired={refreshLinks}
        />
      </div>,
    );
  } else if (!me?.attended && me?.hasRecording) {
    sections.push(
      <div key="rec" className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-rounded tw-border tw-border-line tw-bg-surface-alt tw-p-4">
        <p className="tw-m-0 tw-text-sm tw-text-content">The recording is ready — catch up whenever it suits you.</p>
        <AppButton size="sm" onClick={watch} disabled={busy === "recording"}>
          <PlayCircle size={16} /> Watch the recording
        </AppButton>
      </div>,
    );
  }

  if (me?.whatsappGroupUrl) {
    sections.push(
      <AppButton key="wa" variant="outline" as="a" href={me.whatsappGroupUrl} target="_blank" rel="noopener noreferrer">
        <WhatsAppIcon size={16} /> Join the WhatsApp group
      </AppButton>,
    );
  }

  if (!sections.length && !error) return null;
  return (
    <div className="tw-mb-5 tw-flex tw-flex-col tw-gap-3 tw-text-left">
      {sections}
      {error && <Callout tone="danger" role="alert">{error}</Callout>}
    </div>
  );
};

export default PostSessionSummary;
